import { Logger } from 'common/logger';
import { statusDenormalize, turbineDefaults } from '../routes/Operations/models/helpers/turbines-model';
import { handleStatusData, statusDefaultData } from './helpers/status-model';
import {
    WEATHER_DEFAULT_STATE,
    transformForecast,
    transformLightningRisk,
} from '../routes/Operations/models/weather.utils';
import { keyBy } from 'lodash';
import { authXConfig } from 'router/authx-config';
import { isRealTurbine } from 'common/sites.util';
import { enhanceSiteTurbine } from 'common/sites';
import { addOrUpdateById, addOrUpdatePark, getById } from './rematch.utils';
import { convertToBST } from 'common/dates';
import moment from 'moment';
import axiosInstance from '../utils/axiosInstance';

export const parksData = {
    name: 'parksData',
    state: {
        parkConfigs: {},
        turbinesById: {},
        powerGridsById: {},
        plantData: {},
        systems: {},
        forecastHistoryById: {},
        forecast: {},
        lightningRisk: {},
        weather: {},
        vesselsById: {},
        transferById: {},
        lightningAlerts: {},
    },
    reducers: {
        updateMultipleTurbines(state, payload) {
            const extended = payload.map((turbineRaw) => ({
                ...turbineRaw,
                ...statusDenormalize(turbineRaw),
            }));

            extended.forEach((item) => {
                addOrUpdateById({ state, stateKey: 'turbinesById', payload: item, defaults: turbineDefaults });
            });
        },

        updateMultiplePowerGrids(state, payload) {
            payload.forEach((item) => {
                addOrUpdateById({ state, stateKey: 'powerGridsById', payload: item });
            });
        },
        updatePlantData(state, payload) {
            addOrUpdatePark(state, 'plantData', payload);
        },

        updateTransferData(state, payload) {
            const props = { state, stateKey: 'transferById', payload, payloadKey: 'vesselId.value' };

            const dates = [convertToBST(payload.startDate.value)];
            if (payload.endDate) {
                dates.push(convertToBST(payload.endDate.value));
            }

            const relevantDate = moment.max(...dates);

            const today = convertToBST().startOf('day');
            const isOld = relevantDate.isBefore(today);
            if (isOld) {
                Logger.debug('Transfer data is old, not updating', payload);
                return;
            }

            const oldPayload = getById(props);

            if (!oldPayload?.versionNumber || oldPayload?.versionNumber?.value < payload.versionNumber?.value) {
                addOrUpdateById(props);
            } else {
                Logger.debug('TransferData not updated, versionNumber is the same or lower.', oldPayload, payload);
            }
        },

        updateVesselData(state, payload) {
            addOrUpdateById({
                state,
                stateKey: 'vesselsById',
                payload,
            });
        },
        updateSystemStatus(state, payload) {
            const byIdItem = state.systems[payload.parkName] || [...statusDefaultData];
            const updatedItem = handleStatusData(byIdItem, payload);

            state.systems[payload.parkName] = updatedItem;
        },
        updateLightningAlert(state, payload) {
            state.lightningAlerts[payload.parkName] = payload;
        },
        updateForecastHistory(state, { parkName, entityKey, values }) {
            addOrUpdateById({ state, stateKey: 'forecastHistoryById', payload: { parkName, entityKey, ...values } });
        },

        updateForecast(state, payload) {
            state.forecast[payload.parkName] = {
                ...(state.forecast[payload.parkName] || WEATHER_DEFAULT_STATE.forecast),
                ...transformForecast(state.weather[payload.parkName] || {}, payload, { forecast: 8, historical: 1 }),
            };

            state.lightningRisk[payload.parkName] =
                transformLightningRisk(payload) || WEATHER_DEFAULT_STATE.lightningRisk;
        },
        updateWeather(state, payload) {
            addOrUpdatePark(state, 'weather', payload, WEATHER_DEFAULT_STATE.data);
        },
        updateParkConfigs(state, payload) {
            state.parkConfigs = payload;

            // set default turbines for each park
            Object.keys(state.parkConfigs).forEach((parkName) => {
                const parkData = state.parkConfigs[parkName];

                const turbinesWithDefaults = parkData.turbines.filter(isRealTurbine).map((turbineConf) => ({
                    ...turbineDefaults,
                    ...enhanceSiteTurbine(turbineConf),
                }));
                const allTurbinesById = keyBy(turbinesWithDefaults, (o) => o.id);
                state.turbinesById[parkName] = allTurbinesById;
            });
        },
    },

    effects: (dispatch) => {
        return {
            async loadMultipleTurbines(payload, rootState) {
                Logger.log(`loadMultipleTurbines Bulk updating:`, payload, rootState);

                dispatch.parksData.updateMultipleTurbines(payload);
            },
            async loadMultiplePowerGrids(payload, rootState) {
                Logger.log(`loadMultiplePowerGrids Bulk updating:`, payload, rootState);

                dispatch.parksData.updateMultiplePowerGrids(payload);
            },

            async loadPlantData(payload, rootState) {
                Logger.log(`loadPlantData updating:`, payload, rootState);

                dispatch.parksData.updatePlantData(payload);
            },

            async loadTransferData(payload, rootState) {
                Logger.log(`loadTransferData updating:`, payload, rootState);

                dispatch.parksData.updateTransferData(payload);
            },

            async loadVesselData(payload, rootState) {
                Logger.log(`loadVesselData updating:`, payload, rootState);

                dispatch.parksData.updateVesselData(payload);
            },

            async loadSystemStatus(payload, rootState) {
                Logger.log(`loadSystemStatus updating:`, payload, rootState);

                dispatch.parksData.updateSystemStatus(payload);
            },
            async loadLightningAlert(payload, rootState) {
                Logger.log(`loadSystemStatus updating:`, payload, rootState);

                dispatch.parksData.updateLightningAlert(payload);
            },
            async loadForecastHistory(payload, rootState) {
                Logger.log(`loadForecastHistory updating:`, payload, rootState);

                dispatch.parksData.updateForecastHistory(payload);
            },

            async loadForecast(payload, rootState) {
                Logger.log(`updateForecast updating:`, payload, rootState);

                dispatch.parksData.updateForecast(payload);
            },

            async loadWeather(payload, rootState) {
                Logger.log(`loadWeather updating:`, payload, rootState);

                dispatch.parksData.updateWeather(payload);
            },
            async loadAppConfig({ token }, rootState) {
                Logger.log(`loadAppConfig updating:`, token, rootState);

                try {
                    const { data } = await axiosInstance.get(authXConfig.API.ConfigAPI.parkURL);

                    const parkObjects = data.map((park) => {
                        const { siteDefinition, ...restOfPark } = park;
                        const parkInfo = siteDefinition;
                        return {
                            ...restOfPark,
                            ...parkInfo,
                        };
                    });

                    const parkData = keyBy(parkObjects, (o) => o.datasetName);
                    dispatch.parksData.updateParkConfigs(parkData);
                } catch (err) {
                    Logger.error('Error loading app config', err);
                }
            },
        };
    },
};
